"use client";

import React from "react";
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { ExternalLinkIcon, PlusIcon } from "lucide-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import SearchAndFilterBar from "./filter/searchAndFilterBar";

export type RowObjFamily = {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  totalAmountDue: number;
  totalAmountPaid: number;
};

const columnHelper = createColumnHelper<RowObjFamily>();

export default function FamilyTable(props: {
  tableData: any;
  searchName: string;
  setSearchName: any;
  currentPage: number;
  totalPage: number;
  hasNextPage: boolean;
  handleFetchNextPage: () => void;
  handleFetchPrevPage: () => void;
  isLoading?: boolean;
}) {
  const { tableData, handleFetchNextPage, hasNextPage, handleFetchPrevPage, totalPage, currentPage, isLoading } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns = [
    columnHelper.accessor("name", {
      id: "Nom",
      header: () => <span>Nom</span>,
      cell: (info) => (
        <div>
          <span className="font-medium text-gray-900">{info.getValue()}</span>
          {info.row.original.description && (
            <p className="mt-0.5 max-w-[200px] truncate text-xs text-gray-400">{info.row.original.description}</p>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("memberCount", {
      id: "Membres",
      header: () => <span>Membres</span>,
      cell: (info) => (
        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("totalAmountDue", {
      id: "Solde",
      header: () => <span>Solde dû</span>,
      cell: (info) => {
        const val = info.getValue();
        return (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${val > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
            {val ? `${val.toFixed(2)} \u20AC` : "0,00 \u20AC"}
          </span>
        );
      },
    }),
    columnHelper.accessor("totalAmountPaid", {
      id: "Payé",
      header: () => <span>Total payé</span>,
      cell: (info) => (
        <span className="text-gray-600">{info.getValue() ? `${info.getValue().toFixed(2)} \u20AC` : "0,00 \u20AC"}</span>
      ),
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: () => <span></span>,
      cell: (info) => (
        <a href={`/admin/family/${info.getValue()}`} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
          <ExternalLinkIcon className="h-4 w-4" />
        </a>
      ),
    }),
  ];

  const table = useReactTable({ data: tableData, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">Familles</h3>
        <a href="/admin/family/create" className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700">
          <PlusIcon className="h-3.5 w-3.5" />Nouvelle
        </a>
      </div>
      <div className="px-5 pt-3">
        <SearchAndFilterBar searchValue={props.searchName} onSearchChange={props.setSearchName} />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" /></div>
      ) : tableData.length === 0 ? (
        <div className="py-16 text-center"><p className="text-sm text-gray-400">Aucune famille trouvée</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>{table.getHeaderGroups().map((hg) => (<tr key={hg.id}>{hg.headers.map((header) => (<th key={header.id} onClick={header.column.getToggleSortingHandler()} className="cursor-pointer px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">{flexRender(header.column.columnDef.header, header.getContext())}</th>))}</tr>))}</thead>
            <tbody className="divide-y divide-gray-50">{table.getRowModel().rows.map((row) => (<tr key={row.id} className="transition-colors hover:bg-gray-50/50">{row.getVisibleCells().map((cell) => (<td key={cell.id} className="whitespace-nowrap px-5 py-3 text-sm">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}</tr>))}</tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
        <span className="text-xs text-gray-400">Page {currentPage} sur {totalPage}</span>
        <div className="flex items-center gap-1">
          <button onClick={handleFetchPrevPage} disabled={currentPage <= 1} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"><MdArrowLeft className="h-5 w-5" /></button>
          <button onClick={handleFetchNextPage} disabled={!hasNextPage} className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"><MdArrowRight className="h-5 w-5" /></button>
        </div>
      </div>
    </div>
  );
}
